import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { actAsTenant, currentImpersonation } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Users2, Package, ShieldCheck, Eye, Power, PowerOff, X, Layers, Sparkles, Pencil, LogIn, Trash2 } from "lucide-react";
import ConfirmTypeDialog from "@/components/ConfirmTypeDialog";

export default function Tenants() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [impersonating, setImpersonating] = useState(currentImpersonation());
  const [loading, setLoading] = useState(true);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmImpersonate, setConfirmImpersonate] = useState(null);
  const [editing, setEditing] = useState(null);
  const [signInFor, setSignInFor] = useState(null);   // tenant we want to sign into

  const load = async () => {
    setLoading(true);
    try {
      const [t, m] = await Promise.all([
        api.get(`/tenants${showInactive ? "?include_inactive=true" : ""}`),
        api.get("/tenants-metrics/overview"),
      ]);
      setTenants(t.data);
      setMetrics(m.data);
    } catch { toast.error("Failed to load tenants"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (user?.role === "super_admin") load(); }, [user, showInactive]);

  if (user && user.role !== "super_admin") return <Navigate to="/dashboard" replace />;

  const startImpersonation = (t) => setConfirmImpersonate(t);
  const runImpersonation = async () => {
    const t = confirmImpersonate;
    if (!t) return;
    try {
      await api.post(`/tenants/${t.id}/impersonate`);
      actAsTenant(t.id);
      setImpersonating(t.id);
      toast.success(`Now viewing as ${t.name}`);
      setConfirmImpersonate(null);
      setTimeout(() => { window.location.href = "/dashboard"; }, 400);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const stopImpersonation = () => {
    actAsTenant(null); setImpersonating(null); toast.success("Impersonation cleared");
    setTimeout(() => { window.location.reload(); }, 300);
  };
  const deactivate = (t) => setConfirmDeactivate(t);
  const runDeactivate = async () => {
    await api.delete(`/tenants/${confirmDeactivate.id}`);
    toast.success("Tenant deactivated");
    load();
  };
  const reactivate = async (t) => {
    await api.post(`/tenants/${t.id}/reactivate`); toast.success("Tenant reactivated"); load();
  };
  const purge = (t) => setConfirmDelete(t);
  const runPurge = async () => {
    try {
      await api.delete(`/tenants/${confirmDelete.id}/purge`);
      toast.success("Tenant permanently deleted");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const totals = metrics?.totals || {};

  return (
    <div className="p-6 md:p-8" data-testid="tenants-page">
      {impersonating && (
        <div className="mb-4 p-3 bg-[#FEF3C7] border border-[#FCD34D] text-[#92400E] flex items-center justify-between rounded-md" data-testid="impersonation-banner">
          <div className="flex items-center gap-2 text-sm font-semibold"><Eye className="w-4 h-4" /> You are viewing this workspace as a super-admin impersonator. All changes will affect that tenant.</div>
          <Button onClick={stopImpersonation} size="sm" variant="outline" data-testid="stop-impersonation-btn"><X className="w-4 h-4 mr-1" /> Stop</Button>
        </div>
      )}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Super Admin</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Tenants</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">Each tenant is a fully-isolated workspace for a different organisation. Their data, users, certificates, and templates never mix.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} className="w-4 h-4" data-testid="tenant-show-inactive" />
            Show inactive
          </label>
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button data-testid="new-tenant-btn" className="bg-[#01ADEF] hover:bg-[#0198D6]"><Plus className="w-4 h-4 mr-2" />New Tenant</Button>
            </DialogTrigger>
            <NewTenantDialog onDone={() => { setOpenNew(false); load(); }} />
          </Dialog>
        </div>
      </div>

      <ConfirmTypeDialog
        open={!!confirmDeactivate}
        onOpenChange={(o) => !o && setConfirmDeactivate(null)}
        phrase={confirmDeactivate?.name || ""}
        title="Deactivate this workspace?"
        description="All users in this tenant will lose sign-in access immediately. Their data is preserved and you can reactivate later, but every session and API token belonging to this tenant will be rejected."
        confirmLabel="Deactivate tenant"
        onConfirm={runDeactivate}
        testidPrefix="deactivate-tenant"
      />

      <ConfirmTypeDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        phrase={confirmDelete?.name || ""}
        title="Permanently delete this workspace?"
        description={confirmDelete
          ? `This permanently deletes "${confirmDelete.name}" and ALL of its data: ${confirmDelete.user_count ?? 0} user(s), ${confirmDelete.asset_count ?? 0} asset(s), ${confirmDelete.certificate_count ?? 0} certificate(s), plus every inspection, organisation, template and audit record. This cannot be undone — deactivate instead if you may need the data later.`
          : ""}
        confirmLabel="Delete forever"
        onConfirm={runPurge}
        testidPrefix="delete-tenant"
      />

      <EditTenantDialog
        tenant={editing}
        onOpenChange={(open) => !open && setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />

      <SignInAsDialog
        tenant={signInFor}
        onOpenChange={(open) => !open && setSignInFor(null)}
      />

      <ConfirmTypeDialog
        open={!!confirmImpersonate}
        onOpenChange={(o) => !o && setConfirmImpersonate(null)}
        phrase={confirmImpersonate?.name || ""}
        title={`View ${confirmImpersonate?.name || "workspace"} in read-only mode?`}
        description="You'll see the data exactly as this tenant's admin would, but WRITES are blocked. If you need to make changes, use the 'Sign in as user' action for this tenant. Stop from the top banner when you're done."
        confirmLabel="Enter read-only mode"
        onConfirm={runImpersonation}
        testidPrefix="impersonate-tenant"
      />

      {/* Aggregate KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <Kpi label="Tenants" value={totals.tenants || 0} icon={Layers} tint="#01ADEF" testid="kpi-tenants" />
        <Kpi label="Active" value={totals.tenants_active || 0} icon={ShieldCheck} tint="#059669" testid="kpi-tenants-active" />
        <Kpi label="Users" value={totals.users || 0} icon={Users2} tint="#7C3AED" testid="kpi-users" />
        <Kpi label="Assets" value={totals.assets || 0} icon={Package} tint="#D97706" testid="kpi-assets" />
        <Kpi label="Insp. (30d)" value={totals.inspections_last_30d || 0} icon={Sparkles} tint="#DB2777" testid="kpi-insp-30" />
      </div>

      {/* Tenants list */}
      <div className="bg-white border border-[#E7E5E1] rounded-md overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E7E5E1] flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-tight">All Workspaces</h2>
          <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-gray-500">{tenants.length}</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E7E5E1] bg-[#F5F4F2]">
              <Th>Workspace</Th><Th>Slug</Th><Th className="text-right">Users</Th><Th className="text-right">Assets</Th><Th className="text-right">Certs</Th><Th>Status</Th><Th></Th>
            </tr>
          </thead>
          <tbody data-testid="tenants-table">
            {loading ? <tr><td colSpan={7} className="text-center py-10 text-gray-500">Loading…</td></tr>
              : tenants.length === 0 ? <tr><td colSpan={7} className="text-center py-10 text-gray-500">No tenants yet — create the first one.</td></tr>
              : tenants.map(t => (
                <tr key={t.id} className="border-b border-gray-100 hover:bg-[#FAF9F7]" data-testid={`tenant-row-${t.id}`}>
                  <Td>
                    <div className="font-semibold">{t.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">Created {new Date(t.created_at).toLocaleDateString()}</div>
                  </Td>
                  <Td className="font-mono text-xs text-gray-600">{t.slug}</Td>
                  <Td className="text-right font-mono font-semibold">{t.user_count}</Td>
                  <Td className="text-right font-mono">{t.asset_count}</Td>
                  <Td className="text-right font-mono">{t.certificate_count}</Td>
                  <Td>
                    {t.is_active === false
                      ? <span className="inline-block text-[10px] font-bold px-2 py-1 uppercase tracking-wider border bg-gray-100 text-gray-700 border-gray-300 rounded">Inactive</span>
                      : <span className="inline-block text-[10px] font-bold px-2 py-1 uppercase tracking-wider border bg-[#D1FAE5] text-[#065F46] border-[#6EE7B7] rounded">Active</span>}
                  </Td>
                  <Td className="flex items-center gap-1">
                    <button data-testid={`tenant-edit-${t.id}`} title="Edit workspace" onClick={() => setEditing(t)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 text-gray-700"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`tenant-signin-${t.id}`} title="Sign in as a user in this tenant" disabled={t.is_active === false} onClick={() => setSignInFor(t)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 text-[#0284C7] disabled:opacity-40 disabled:cursor-not-allowed"><LogIn className="w-4 h-4" /></button>
                    <button data-testid={`tenant-impersonate-${t.id}`} title="View as this tenant (read-only)" onClick={() => startImpersonation(t)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 text-[#01ADEF]"><Eye className="w-4 h-4" /></button>
                    {t.is_active === false
                      ? <button data-testid={`tenant-reactivate-${t.id}`} title="Reactivate" onClick={() => reactivate(t)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 text-[#059669]"><Power className="w-4 h-4" /></button>
                      : <button data-testid={`tenant-deactivate-${t.id}`} title="Deactivate" onClick={() => deactivate(t)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 text-[#DC2626]"><PowerOff className="w-4 h-4" /></button>}
                    <button data-testid={`tenant-delete-${t.id}`} title="Delete permanently" onClick={() => purge(t)} className="w-8 h-8 flex items-center justify-center hover:bg-[#FEE2E2] text-[#B91C1C]"><Trash2 className="w-4 h-4" /></button>
                  </Td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewTenantDialog({ onDone }) {
  const [form, setForm] = useState({ name: "", slug: "", notes: "", admin_email: "", admin_name: "", admin_password: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const payload = { name: form.name, slug: form.slug || undefined, notes: form.notes || undefined };
      if (form.admin_email) payload.admin = { email: form.admin_email, name: form.admin_name || undefined, password: form.admin_password || undefined };
      const { data } = await api.post("/tenants", payload);
      setResult(data);
      toast.success("Tenant created");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create tenant"); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent className="rounded-md max-w-lg" data-testid="new-tenant-dialog">
      <DialogHeader>
        <DialogTitle className="font-display flex items-center gap-2"><Building2 className="w-5 h-5 text-[#01ADEF]" />New Tenant</DialogTitle>
        <DialogDescription className="text-xs text-gray-500">Provision a brand-new fully isolated workspace. Optionally seed the first admin now.</DialogDescription>
      </DialogHeader>
      {result ? (
        <div className="space-y-3 py-2 text-sm">
          <div className="bg-[#D1FAE5] border border-[#6EE7B7] text-[#065F46] rounded px-3 py-2">Tenant <b>{result.tenant?.name}</b> created.</div>
          {result.admin && (
            <div className="text-xs bg-[#F5F4F2] p-3 rounded font-mono">
              <div>Admin email: <b>{result.admin.email}</b></div>
              <div>Temp password: <b>{result.admin.password}</b> <span className="text-gray-500">(share securely)</span></div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3 py-2">
          <div><Label className="text-xs uppercase tracking-wider font-semibold">Workspace Name *</Label>
            <Input data-testid="nt-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs uppercase tracking-wider font-semibold">Slug (URL-safe)</Label>
            <Input data-testid="nt-slug" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated from name" className="mt-1 font-mono" /></div>
          <div><Label className="text-xs uppercase tracking-wider font-semibold">Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
          <div className="border-t border-[#E7E5E1] pt-3">
            <div className="text-xs uppercase tracking-wider font-semibold text-gray-600 mb-2">Seed First Admin (optional)</div>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="nt-admin-email" placeholder="admin@newco.com" value={form.admin_email} onChange={e => setForm({ ...form, admin_email: e.target.value })} />
              <Input data-testid="nt-admin-name" placeholder="Admin name" value={form.admin_name} onChange={e => setForm({ ...form, admin_name: e.target.value })} />
            </div>
            <Input data-testid="nt-admin-password" type="text" placeholder="Temporary password (min 6)" value={form.admin_password} onChange={e => setForm({ ...form, admin_password: e.target.value })} className="mt-2" />
          </div>
        </div>
      )}
      <DialogFooter>
        {result ? (
          <Button onClick={onDone} className="bg-[#01ADEF] hover:bg-[#0198D6]">Done</Button>
        ) : (
          <>
            <Button variant="outline" onClick={onDone}>Cancel</Button>
            <Button data-testid="nt-submit" disabled={busy} onClick={submit} className="bg-[#01ADEF] hover:bg-[#0198D6]">{busy ? "Creating…" : "Create Tenant"}</Button>
          </>
        )}
      </DialogFooter>
    </DialogContent>
  );
}

const LAST_SIGNIN_USER_KEY = (tid) => `docq_last_signin_user_${tid}`;

function EditTenantDialog({ tenant, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: "", slug: "", notes: "", brand_primary: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (tenant) {
      setForm({
        name: tenant.name || "",
        slug: tenant.slug || "",
        notes: tenant.notes || "",
        brand_primary: tenant.settings?.brand_primary || "#01ADEF",
      });
    }
  }, [tenant]);

  const save = async () => {
    if (!tenant) return;
    if (!form.name.trim()) return toast.error("Workspace name is required");
    setSaving(true);
    try {
      const payload = {};
      if (form.name !== tenant.name) payload.name = form.name.trim();
      if (form.slug !== tenant.slug) payload.slug = form.slug.trim();
      if (form.notes !== (tenant.notes || "")) payload.notes = form.notes;
      const curBrand = tenant.settings?.brand_primary || "#01ADEF";
      if (form.brand_primary !== curBrand) payload.brand_primary = form.brand_primary;
      if (Object.keys(payload).length === 0) { toast("No changes"); setSaving(false); return; }
      await api.patch(`/tenants/${tenant.id}`, payload);
      toast.success("Workspace updated");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!tenant} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-lg" data-testid="edit-tenant-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Building2 className="w-5 h-5 text-[#01ADEF]" />Edit {tenant?.name}</DialogTitle>
          <DialogDescription className="text-xs text-gray-500">Change core workspace details. Use the sign-in action on the row to enter this tenant as one of its users.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Workspace Name</Label>
            <Input data-testid="edit-tenant-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Slug (URL-safe)</Label>
            <Input data-testid="edit-tenant-slug" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} className="mt-1 font-mono" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Notes</Label>
            <Textarea data-testid="edit-tenant-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Brand Colour</Label>
            <div className="flex items-center gap-2 mt-1">
              <input data-testid="edit-tenant-brand" type="color" value={form.brand_primary} onChange={e => setForm({ ...form, brand_primary: e.target.value.toLowerCase() })} className="h-9 w-14 border border-gray-300 cursor-pointer" />
              <Input value={form.brand_primary} onChange={e => setForm({ ...form, brand_primary: e.target.value.toLowerCase() })} className="font-mono" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>Close</Button>
          <Button data-testid="edit-tenant-save" disabled={saving} onClick={save} className="bg-[#01ADEF] hover:bg-[#0198D6]">{saving ? "Saving…" : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SignInAsDialog({ tenant, onOpenChange }) {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [confirmSignin, setConfirmSignin] = useState(false);

  useEffect(() => {
    if (!tenant) { setUsers([]); setSelectedUserId(""); return; }
    setSelectedUserId("");
    setLoadingUsers(true);
    api.get(`/tenants/${tenant.id}/users`)
      .then(({ data }) => {
        setUsers(data);
        let last = null;
        try { last = localStorage.getItem(LAST_SIGNIN_USER_KEY(tenant.id)); } catch { /* private mode */ }
        const stillExists = last && data.find(u => u.id === last);
        const admin = data.find(u => u.role === "admin") || data[0];
        const chosen = stillExists ? last : (admin ? admin.id : "");
        if (chosen) setSelectedUserId(chosen);
      })
      .catch((e) => { console.debug("users fetch failed", e?.message); setUsers([]); })
      .finally(() => setLoadingUsers(false));
  }, [tenant]);

  const signInToTenant = async () => {
    if (!tenant) return;
    if (!selectedUserId) return toast.error("Please pick a user to sign in as");
    setSigningIn(true);
    try {
      const { data } = await api.post(`/tenants/${tenant.id}/sign-in-as`, null, {
        params: { user_id: selectedUserId },
      });
      try { localStorage.setItem(LAST_SIGNIN_USER_KEY(tenant.id), selectedUserId); } catch { /* ignore */ }
      toast.success(data.message || "Signed in");
      try { localStorage.removeItem("act_as_tenant"); } catch { /* ignore */ }
      setTimeout(() => { window.location.href = "/dashboard"; }, 400);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      setSigningIn(false);
    }
  };

  const selectedEmail = users.find(u => u.id === selectedUserId)?.email;

  return (
    <>
      <Dialog open={!!tenant} onOpenChange={onOpenChange}>
        <DialogContent className="rounded-md max-w-lg" data-testid="signin-tenant-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><LogIn className="w-5 h-5 text-[#0284C7]" />Sign in to {tenant?.name}</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Take over the session of any user in this workspace. You&apos;ll operate the tenant directly with write access.
              Every use is audit-logged; a one-click <b>Return to Super-Admin</b> banner brings you back.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Choose user</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId} disabled={loadingUsers || users.length === 0}>
                <SelectTrigger className="mt-1 rounded-md" data-testid="signin-user-select">
                  <SelectValue placeholder={loadingUsers ? "Loading users…" : users.length === 0 ? "No users in this tenant" : "Select a user"} />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id} data-testid={`signin-user-option-${u.id}`}>
                      <span className="font-mono text-xs uppercase mr-2 text-gray-500">{u.role}</span>
                      <span>{u.name || u.email}</span>
                      {u.name && <span className="text-gray-400 ml-1 text-xs">— {u.email}</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {tenant?.is_active === false && <p className="text-[11px] text-[#B91C1C]">Reactivate this workspace first before signing in.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange?.(false)}>Cancel</Button>
            <Button data-testid="signin-submit-btn" onClick={() => setConfirmSignin(true)} disabled={signingIn || !tenant?.is_active || !selectedUserId} className="bg-[#0284C7] hover:bg-[#0369A1]">
              <LogIn className="w-4 h-4 mr-2" />{signingIn ? "Signing in…" : (selectedEmail ? `Sign in as ${selectedEmail}` : "Sign in")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmTypeDialog
        open={confirmSignin}
        onOpenChange={setConfirmSignin}
        phrase={tenant?.name || ""}
        title={`Sign in as ${selectedEmail || "user"}?`}
        description={`Your super-admin session will be swapped for ${selectedEmail || "this user"}'s session in ${tenant?.name || "the tenant"}. A 30-minute return token is issued so you can come back in one click. Every use is audit-logged.`}
        confirmLabel="Sign in as this user"
        onConfirm={async () => { await signInToTenant(); }}
        testidPrefix="signin-tenant"
      />
    </>
  );
}

const Th = ({ children, className = "" }) => <th className={`text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600 ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const Kpi = ({ label, value, icon: Icon, tint, testid }) => (
  <div className="bg-white border border-[#E7E5E1] p-4 rounded-md" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">{label}</span>
      <div className="w-7 h-7 flex items-center justify-center rounded-full" style={{ background: tint }}><Icon className="w-3.5 h-3.5 text-white" strokeWidth={2.5} /></div>
    </div>
    <div className="font-display text-3xl font-bold tracking-tight" style={{ color: "#FE5000" }}>{value}</div>
  </div>
);
